import { createContext, CSSProperties, MouseEvent, ReactNode, useContext } from "react";

/** A column of a queue list: its heading and its width (a CSS grid track) on a wide screen. */
export interface QueueColumn {
  heading: string;
  width: string;
}

/** The breakpoint from which a queue list reads as a table; below it every row is a card. */
export type TableFrom = "md" | "xl";

// What each breakpoint adds to the list. The class names are written out in full because Tailwind finds them by
// reading the source. The columns are laid out by a CSS grid, and the widths come through a variable on the table.
const LAYOUTS: Record<
  TableFrom,
  {
    minWidthQuery: string;
    grid: string;
    rowgroup: string;
    row: string;
    tappableRow: string;
    titleCell: string;
    detailCell: string;
    cellLabel: string;
  }
> = {
  md: {
    minWidthQuery: "(min-width: 768px)",
    grid: "md:grid md:[grid-template-columns:var(--queue-columns)]",
    rowgroup: "space-y-2 md:space-y-0",
    row: "md:gap-3 md:rounded-none md:border-0 md:border-b md:bg-transparent md:px-3 md:py-2.5",
    tappableRow: "cursor-pointer hover:bg-gray-50 md:cursor-auto md:hover:bg-transparent",
    titleCell: "md:w-auto",
    detailCell: "md:text-sm",
    cellLabel: "md:hidden",
  },
  xl: {
    minWidthQuery: "(min-width: 1280px)",
    grid: "xl:grid xl:[grid-template-columns:var(--queue-columns)]",
    rowgroup: "space-y-2 xl:space-y-0",
    row: "xl:gap-3 xl:rounded-none xl:border-0 xl:border-b xl:bg-transparent xl:px-3 xl:py-2.5",
    tappableRow: "cursor-pointer hover:bg-gray-50 xl:cursor-auto xl:hover:bg-transparent",
    titleCell: "xl:w-auto",
    detailCell: "xl:text-sm",
    cellLabel: "xl:hidden",
  },
};

// The rows and cells lay themselves out by the breakpoint the table chose.
const TableFromContext = createContext<TableFrom>("md");

/**
 * The list of a queue page. It reads as a table from the `tableFrom` breakpoint up (`md`, or `xl` for a list with
 * many columns that need the room) and as a stack of cards below it: the same markup, laid out by `columns` on a
 * wide screen and by wrapping on a narrow one. The cells of every row must follow the order of `columns`.
 */
export function QueueTable({
  label,
  columns,
  tableFrom = "md",
  children,
}: {
  label: string;
  columns: QueueColumn[];
  tableFrom?: TableFrom;
  children: ReactNode;
}) {
  const layout = LAYOUTS[tableFrom];
  const widths = { "--queue-columns": columns.map((column) => column.width).join(" ") } as CSSProperties;

  return (
    <TableFromContext.Provider value={tableFrom}>
      <div role="table" aria-label={label} style={widths}>
        <div
          role="row"
          className={`hidden gap-3 border-b border-gray-200 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 ${layout.grid}`}
        >
          {columns.map((column, index) => (
            <div key={index} role="columnheader">
              {column.heading}
            </div>
          ))}
        </div>
        <div role="rowgroup" className={layout.rowgroup}>
          {children}
        </div>
      </div>
    </TableFromContext.Provider>
  );
}

/**
 * One row of the list. With `onOpen` a card opens the document when tapped anywhere (the buttons and links inside
 * keep their own action, so a tap on them is not also a tap on the card). In the table layout the row stays plain:
 * there the button in its last cell is the way in, and selecting text in a row must not open anything.
 */
export function QueueRow({ onOpen, children }: { onOpen?: () => void; children: ReactNode }) {
  const layout = LAYOUTS[useContext(TableFromContext)];

  const openUnlessOnControl = (event: MouseEvent<HTMLDivElement>) => {
    if (!onOpen || (event.target as HTMLElement).closest("a, button")) return;
    if (window.matchMedia(layout.minWidthQuery).matches) return;
    onOpen();
  };

  return (
    <div
      role="row"
      onClick={openUnlessOnControl}
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-gray-200 bg-white p-3 ${layout.row} ${layout.grid} ${
        onOpen ? layout.tappableRow : ""
      }`}
    >
      {children}
    </div>
  );
}

/**
 * One cell of a row: the title, or a quieter detail. A `label` names the value in the card
 * layout, where the column headings are not shown.
 */
export function QueueCell({
  isTitle = false,
  label,
  className = "",
  children,
}: {
  isTitle?: boolean;
  label?: string;
  className?: string;
  children?: ReactNode;
}) {
  const layout = LAYOUTS[useContext(TableFromContext)];
  const size = isTitle
    ? `w-full text-sm font-medium text-gray-900 ${layout.titleCell}`
    : `text-xs text-gray-600 ${layout.detailCell}`;

  return (
    <div role="cell" className={`min-w-0 ${size} ${className}`}>
      {label && children && <span className={`text-gray-500 ${layout.cellLabel}`}>{label}: </span>}
      {children}
    </div>
  );
}
